import { FirebaseApp } from '@angular/fire/app';
import { Auth, connectAuthEmulator, getAuth, signOut } from '@angular/fire/auth';
import { FIREBASE_EMULATOR_HOST, FIREBASE_EMULATOR_PORTS } from './firebase.client.config';

/** Match the page hostname so popup OAuth redirect URLs include apiKey + providerId. */
export function getEmulatorAuthUrl(): string {
	const host =
		typeof window !== 'undefined' && window.location.hostname === '127.0.0.1'
			? '127.0.0.1'
			: FIREBASE_EMULATOR_HOST;
	return `http://${host}:${FIREBASE_EMULATOR_PORTS.auth}`;
}

const STALE_EMULATOR_AUTH_CODES = new Set([
	'auth/id-token-revoked',
	'auth/user-token-expired',
	'auth/invalid-user-token',
	'auth/invalid-credential',
]);

export function isLocalFirebaseEmulatorHost(): boolean {
	if (typeof window === 'undefined') {
		return false;
	}
	const host = window.location.hostname;
	return host === 'localhost' || host === '127.0.0.1';
}

export function shouldUseFirebaseEmulators(useFirebaseEmulators: boolean): boolean {
	return !!useFirebaseEmulators && isLocalFirebaseEmulatorHost();
}

export function isStaleEmulatorAuthError(code: string | undefined): boolean {
	return !!code && STALE_EMULATOR_AUTH_CODES.has(code);
}

/**
 * Factory for Angular `provideAuth` on apps that do **not** use MAS Google popup sign-in
 * (e.g. `my`, dashboard, kiosk). **MAS must not use this** — use inline `getAuth()` without
 * an app argument; see `docs/emulator-troubleshooting.md` (“Angular Auth emulator”).
 */
export function createFirebaseAuth(app: FirebaseApp, useFirebaseEmulators: boolean): Auth {
	if (!shouldUseFirebaseEmulators(useFirebaseEmulators)) {
		return getAuth(app);
	}

	const auth = getAuth(app);
	connectAuthEmulator(auth, getEmulatorAuthUrl(), { disableWarnings: true });
	void purgeStaleEmulatorAuthSession(auth);
	return auth;
}

export async function purgeStaleEmulatorAuthSession(auth: Auth): Promise<void> {
	const user = auth.currentUser;
	if (!user) {
		return;
	}

	try {
		await user.getIdToken(true);
	} catch (error: unknown) {
		const code = (error as { code?: string })?.code;
		if (!isStaleEmulatorAuthError(code)) {
			return;
		}
		await signOut(auth);
		console.warn(
			'[firebase] Cleared a stale auth session (common after restarting the Auth emulator). Sign in again.',
		);
	}
}
